import {FC, useCallback, useLayoutEffect, useMemo, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {Box, Button, Divider, Switch, TextField, Typography} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
	GRID_CHECKBOX_SELECTION_FIELD,
	GridPanelContent,
	GridPanelWrapper,
	gridColumnDefinitionsSelector,
	gridColumnVisibilityModelSelector,
	useGridApiContext,
	useGridRootProps,
	useGridSelector
} from "@mui/x-data-grid-pro";
import type {GridColumnsPanelProps} from "@mui/x-data-grid/components/panel/GridColumnsPanel";
import type {GridStateColDef} from "@mui/x-data-grid/models/colDef/gridColDef";

type ColWithPanelMeta = GridStateColDef & {columnPanelGroup?: string};

function panelGroup(col: GridStateColDef): string {
	const g = (col as ColWithPanelMeta).columnPanelGroup;
	return (typeof g === "string" && g.trim()) ? g.trim() : "";
}

function groupSectionOrder(fields: string[], colByField: Map<string, GridStateColDef>): string[] {
	const seen = new Set<string>();
	const order: string[] = [];
	for (const f of fields) {
		const col = colByField.get(f);
		if (col === undefined) {
			continue;
		}
		const g = panelGroup(col);
		const key = g || "__ungrouped__";
		if (!seen.has(key)) {
			seen.add(key);
			order.push(g);
		}
	}
	return order;
}

type ColumnReorderApi = {
	getColumnIndex: (field: string) => number;
	setColumnIndex: (field: string, targetIndex: number) => void;
};

const useStyles = makeStyles()((theme) => {
	const panelBg =
		theme.palette.background.paperElevation2 ?? theme.palette.background.paper;

	const panelBorder = "1px solid rgba(255,255,255,0.12)";

	return {
		panelRoot: {
			display: "flex",
			flexDirection: "column",
			flex: 1,
			minWidth: 300,
			maxWidth: 360,
			boxSizing: "border-box",
			backgroundColor: panelBg,
			color: theme.palette.common.white,
			border: panelBorder,
			borderRadius: "6px",
			overflow: "hidden"
		},
		headerSection: {
			paddingLeft: theme.spacing(1.5),
			paddingRight: theme.spacing(1.5),
			paddingTop: theme.spacing(1.5),
			paddingBottom: theme.spacing(1)
		},
		columnsTitle: {
			display: "block",
			color: theme.palette.text.disabled,
			fontWeight: 600,
			letterSpacing: "0.08em",
			marginBottom: theme.spacing(1)
		},
		searchField: {
			"& .MuiOutlinedInput-root": {
				backgroundColor: "rgba(0,0,0,0.25)",
				color: theme.palette.common.white,
				"& fieldset": {
					borderColor: "rgba(255,255,255,0.12)"
				},
				"&:hover fieldset": {
					borderColor: "rgba(255,255,255,0.2)"
				}
			},
			"& .MuiInputBase-input::placeholder": {
				color: theme.palette.text.secondary,
				opacity: 1
			}
		},
		panelContent: {
			maxHeight: 320,
			backgroundColor: panelBg,
			flex: "1 1 auto",
			minHeight: 0,
			width: "100%",
			boxSizing: "border-box"
		},
		listOuter: {
			paddingLeft: theme.spacing(1),
			paddingRight: theme.spacing(1),
			paddingBottom: theme.spacing(1)
		},
		sectionDivider: {
			borderColor: "rgba(255,255,255,0.08)",
			marginTop: theme.spacing(1.25),
			marginBottom: theme.spacing(1.25),
			/* Bleed to panel edges: cancel listOuter horizontal padding */
			marginLeft: theme.spacing(-1),
			marginRight: theme.spacing(-1),
			opacity: 1
		},
		groupTitle: {
			display: "block",
			paddingLeft: theme.spacing(0.5),
			paddingRight: theme.spacing(0.5),
			paddingTop: theme.spacing(0.75),
			paddingBottom: theme.spacing(0.75),
			color: theme.palette.text.disabled,
			fontWeight: 600,
			letterSpacing: "0.08em"
		},
		columnRow: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(0.5),
			paddingTop: theme.spacing(0.5),
			paddingBottom: theme.spacing(0.5),
			paddingLeft: theme.spacing(0.5),
			paddingRight: theme.spacing(0.5),
			borderRadius: theme.spacing(0.5),
			"&:hover": {
				backgroundColor: "rgba(255,255,255,0.04)"
			}
		},
		columnRowClickable: {
			cursor: "pointer"
		},
		dragHandle: {
			display: "flex",
			alignItems: "center",
			cursor: "grab",
			"&:active": {
				cursor: "grabbing"
			}
		},
		dragIcon: {
			fontSize: 20
		},
		dragSpacer: {
			width: 28
		},
		columnLabel: {
			flex: 1
		},
		columnLabelVisible: {
			fontWeight: 700,
			color: theme.palette.common.white
		},
		columnLabelHidden: {
			fontWeight: 400,
			color: "rgba(255,255,255,0.35)"
		},
		columnSwitch: {
			"& .MuiSwitch-switchBase.Mui-checked": {
				color: theme.palette.primary.main
			},
			"& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
				backgroundColor: theme.palette.primary.main,
				opacity: 0.9
			},
			"& .MuiSwitch-track": {
				backgroundColor: "rgba(255,255,255,0.15)"
			}
		},
		lockSlot: {
			width: 58,
			height: 38,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			flexShrink: 0
		},
		lockIcon: {
			fontSize: 22,
			color: theme.palette.action.disabled
		},
		footer: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: theme.spacing(1),
			paddingLeft: theme.spacing(1.5),
			paddingRight: theme.spacing(1.5),
			paddingTop: theme.spacing(1.25),
			paddingBottom: theme.spacing(1.25),
			borderTop: "1px solid rgba(255,255,255,0.08)",
			flexShrink: 0,
			width: "100%",
			boxSizing: "border-box"
		},
		footerSummary: {
			color: theme.palette.text.disabled
		},
		footerCount: {
			color: theme.palette.primary.main,
			fontWeight: 700
		},
		footerActions: {
			display: "flex",
			gap: theme.spacing(1)
		},
		toolbarButton: {
			fontWeight: 700,
			fontSize: 11,
			letterSpacing: "0.06em",
			minWidth: 72
		},
		applyButton: {
			boxShadow: "none",
			"&:hover": {
				boxShadow: "none"
			}
		}
	};
});

export const AdminTableColumnsPanel: FC<GridColumnsPanelProps> = (props) => {
	const {classes, cx} = useStyles();
	const apiRef = useGridApiContext();
	const rootProps = useGridRootProps();
	const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
	const liveVisibility = useGridSelector(apiRef, gridColumnVisibilityModelSelector);

	const [search, setSearch] = useState("");
	const [draftVisibility, setDraftVisibility] = useState<Record<string, boolean | undefined>>({});
	const [openingVisibility, setOpeningVisibility] = useState<Record<string, boolean | undefined>>({});
	const [draftOrder, setDraftOrder] = useState<string[]>([]);
	const [openingOrder, setOpeningOrder] = useState<string[]>([]);

	useLayoutEffect(() => {
		const orderedFields = apiRef.current.getAllColumns().map((c) => c.field);
		setDraftOrder(orderedFields);
		setOpeningOrder([...orderedFields]);
		const vis = {...liveVisibility};
		setDraftVisibility(vis);
		setOpeningVisibility({...vis});
	}, []);

	const colByField = useMemo(() => new Map(columns.map((c) => [c.field, c])), [columns]);

	const searchLower = search.trim().toLowerCase();
	const matchesSearch = useCallback(
		(col: GridStateColDef) => {
			if (!searchLower) return true;
			const label = (col.headerName || col.field).toLowerCase();
			return label.includes(searchLower);
		},
		[searchLower]
	);

	const orderedColumns = useMemo(() => {
		const list: GridStateColDef[] = [];
		for (const f of draftOrder) {
			const c = colByField.get(f);
			if (c) list.push(c);
		}
		for (const c of columns) {
			if (!draftOrder.includes(c.field)) list.push(c);
		}
		return list;
	}, [columns, colByField, draftOrder]);

	const visibleOrderedColumns = useMemo(
		() =>
			orderedColumns
				.filter((col) => col.field !== GRID_CHECKBOX_SELECTION_FIELD)
				.filter(matchesSearch),
		[orderedColumns, matchesSearch]
	);

	const groupKeys = useMemo(
		() => groupSectionOrder(draftOrder, colByField),
		[draftOrder, colByField]
	);

	const groupKeysToRender = useMemo(
		() => groupKeys.filter((g) => visibleOrderedColumns.some((col) => panelGroup(col) === g)),
		[groupKeys, visibleOrderedColumns]
	);

	const hideableColumns = useMemo(() => columns.filter((c) => c.hideable !== false), [columns]);
	const shownHideableCount = useMemo(
		() => hideableColumns.filter((c) => draftVisibility[c.field] !== false).length,
		[draftVisibility, hideableColumns]
	);

	const toggleColumn = (field: string): void => {
		const col = colByField.get(field);
		if (!col || col.hideable === false) return;
		setDraftVisibility((prev) => {
			const next = {...prev};
			const visible = next[field] !== false;
			if (visible) next[field] = false;
			else delete next[field];
			return next;
		});
	};

	const handleDragStart = (e: React.DragEvent, field: string): void => {
		e.dataTransfer.setData("text/plain", field);
		e.dataTransfer.effectAllowed = "move";
	};

	const handleDragOver = (e: React.DragEvent): void => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	};

	const handleDropOn = (e: React.DragEvent, targetField: string): void => {
		e.preventDefault();
		const sourceField = e.dataTransfer.getData("text/plain");
		if (!sourceField || sourceField === targetField) return;
		setDraftOrder((prev) => {
			const next = [...prev];
			const si = next.indexOf(sourceField);
			const ti = next.indexOf(targetField);
			if (si < 0 || ti < 0) return prev;
			next.splice(si, 1);
			next.splice(ti, 0, sourceField);
			return next;
		});
	};

	const handleReset = (): void => {
		setDraftVisibility({...openingVisibility});
		setDraftOrder([...openingOrder]);
	};

	const handleApply = (): void => {
		apiRef.current.setColumnVisibilityModel(draftVisibility);
		const api = apiRef.current as unknown as ColumnReorderApi;
		for (let i = 0; i < draftOrder.length; i++) {
			const field = draftOrder[i];
			const current = api.getColumnIndex(field);
			if (current !== i) {
				api.setColumnIndex(field, i);
			}
		}
		apiRef.current.hidePreferences();
	};

	const showDragHandles = !rootProps.disableColumnReorder;

	return (
		<GridPanelWrapper {...props}>
			<Box className={classes.panelRoot}>
				<Box className={classes.headerSection}>
					<Typography variant="caption" className={classes.columnsTitle}>
						COLUMNS
					</Typography>
					<TextField
						className={classes.searchField}
						size="small"
						fullWidth
						placeholder="Column title"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						variant="outlined"
						autoFocus
					/>
				</Box>
				<GridPanelContent className={classes.panelContent}>
					<Box className={classes.listOuter}>
						{groupKeysToRender.map((groupLabel, groupIndex) => (
							<Box key={groupLabel || "__ungrouped__"}>
								{groupIndex > 0 ? (
									<Divider className={classes.sectionDivider} />
								) : null}
								{groupLabel ? (
									<Typography variant="caption" className={classes.groupTitle}>
										{groupLabel.toUpperCase()}
									</Typography>
								) : null}
								{visibleOrderedColumns
									.filter((col) => panelGroup(col) === groupLabel)
									.map((column) => {
										const isVisible = draftVisibility[column.field] !== false;
										const canHide = column.hideable !== false;
										return (
											<Box
												key={column.field}
												className={cx(classes.columnRow, canHide && classes.columnRowClickable)}
												onClick={canHide ? () => toggleColumn(column.field) : undefined}
												onDragOver={showDragHandles ? handleDragOver : undefined}
												onDrop={showDragHandles ? (e) => handleDropOn(e, column.field) : undefined}
											>
												{showDragHandles ? (
													<Box
														component="span"
														className={classes.dragHandle}
														draggable
														onClick={(e: React.MouseEvent) => e.stopPropagation()}
														onDragStart={(e: React.DragEvent) => handleDragStart(e, column.field)}
													>
														<DragIndicatorIcon
															className={cx(classes.dragIcon)}
															color={"disabled"}
														/>
													</Box>
												) : (
													<Box className={classes.dragSpacer} />
												)}
												<Typography
													variant="body2"
													className={cx(
														classes.columnLabel,
														isVisible ? classes.columnLabelVisible : classes.columnLabelHidden
													)}
												>
													{column.headerName || column.field}
												</Typography>
												{canHide ? (
													<Switch
														size="small"
														className={classes.columnSwitch}
														checked={isVisible}
														onChange={() => toggleColumn(column.field)}
														onClick={(e: React.MouseEvent) => e.stopPropagation()}
													/>
												) : (
													<Box
														component="span"
														className={classes.lockSlot}
														role="img"
														aria-label="This column cannot be hidden"
														title="This column cannot be hidden"
													>
														<LockOutlinedIcon className={classes.lockIcon} />
													</Box>
												)}
											</Box>
										);
									})}
							</Box>
						))}
					</Box>
				</GridPanelContent>
				<Box className={classes.footer}>
					<Typography variant="caption" className={classes.footerSummary}>
						<Box component="span" className={classes.footerCount}>
							{shownHideableCount}
						</Box>
						{" "}
						of {hideableColumns.length} shown
					</Typography>
					<Box className={classes.footerActions}>
						<Button
							size="small"
							variant="outlined"
							color="primary"
							className={classes.toolbarButton}
							onClick={handleReset}
						>
							RESET
						</Button>
						<Button
							size="small"
							variant="contained"
							color="primary"
							className={cx(classes.toolbarButton, classes.applyButton)}
							onClick={handleApply}
						>
							APPLY
						</Button>
					</Box>
				</Box>
			</Box>
		</GridPanelWrapper>
	);
};
